import "twin.macro";

import { FormProvider, useForm, useFormContext } from "react-hook-form";

import { Close } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";

import _ from "lodash";

import { Type } from "@features/ui";
import { OptimizedOrganization } from "@models/Organization";

import { FormData } from "./DistributorForm";
import DistributorUserEntitySelector from "./DistributorUserEntitySelector";

type BulkEditFormData = {
  brandIds: string[];
  channelIds: string[];
  territoryIds: string[];
  bulk: {
    limitedBrands: string[];
    channelIds: string[];
    territoryIds: string[];
  };
};

const BulkEditForm = ({
  distributorOrg,
}: {
  distributorOrg: OptimizedOrganization;
}) => {
  return (
    <div tw="space-y-4">
      <div>
        <Type.P tw="font-medium mb-2">Brands</Type.P>
        <DistributorUserEntitySelector
          distFieldName="brandIds"
          userFieldName="bulk.limitedBrands"
          label="brands"
          orgOptions={distributorOrg.brands}
          limitTags={5}
        />
      </div>

      {distributorOrg.channels.length > 0 && (
        <div>
          <Type.P tw="font-medium mb-2">Channels</Type.P>
          <DistributorUserEntitySelector
            distFieldName="channelIds"
            userFieldName="bulk.channelIds"
            label="channels"
            orgOptions={distributorOrg.channels}
            limitTags={5}
          />
        </div>
      )}

      <div>
        <Type.P tw="font-medium mb-2">Territories</Type.P>
        <DistributorUserEntitySelector
          distFieldName="territoryIds"
          userFieldName="bulk.territoryIds"
          label="territories"
          orgOptions={distributorOrg.territories}
          limitTags={5}
        />
      </div>
    </div>
  );
};

const BulkEditUsersModal = ({
  open,
  onClose,
  selectedUserIds,
  distributorOrg,
}: {
  open: boolean;
  onClose: () => void;
  selectedUserIds: string[];
  distributorOrg: OptimizedOrganization;
}) => {
  const { setValue, getValues } = useFormContext<FormData>();

  // Create a separate form for the bulk edit modal
  const bulkForm = useForm<BulkEditFormData>({
    defaultValues: {
      brandIds: getValues(
        `distributorOrganizations.${distributorOrg.id}.brandIds`
      ),
      channelIds: getValues(
        `distributorOrganizations.${distributorOrg.id}.channelIds`
      ),
      territoryIds: getValues(
        `distributorOrganizations.${distributorOrg.id}.territoryIds`
      ),
      bulk: {
        limitedBrands: [],
        channelIds: [],
        territoryIds: [],
      },
    },
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
  } = bulkForm;

  const onSubmit = (data: BulkEditFormData) => {
    selectedUserIds.forEach((userId) => {
      // Apply bulk updates only for fields that have values selected
      if (data.bulk.limitedBrands.length > 0) {
        setValue(
          `orderers.${userId}.limitedBrands`,
          _(getValues(`orderers.${userId}.limitedBrands`))
            .difference(distributorOrg.brands.map((b) => b.id))
            .concat(data.bulk.limitedBrands)
            .value(),
          {
            shouldDirty: true,
          }
        );
      }
      if (data.bulk.channelIds.length > 0) {
        setValue(
          `orderers.${userId}.channelIds`,
          _(getValues(`orderers.${userId}.channelIds`))
            .difference(distributorOrg.channels.map((c) => c.id))
            .concat(data.bulk.channelIds)
            .value(),
          {
            shouldDirty: true,
          }
        );
      }
      if (data.bulk.territoryIds.length > 0) {
        setValue(
          `orderers.${userId}.territoryIds`,
          _(getValues(`orderers.${userId}.territoryIds`))
            .difference(distributorOrg.territories.map((t) => t.id))
            .concat(data.bulk.territoryIds)
            .value(),
          {
            shouldDirty: true,
          }
        );
      }
    });

    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
      <FormProvider {...bulkForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle tw="flex justify-between items-center">
            Bulk Edit {selectedUserIds.length} Users
            <IconButton onClick={handleCancel} edge="end">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent tw="space-y-6">
            <Type.P tw="text-neutral-600">
              Select the values to apply to all {selectedUserIds.length}{" "}
              selected users. Only fields with selections will be updated.
            </Type.P>

            <BulkEditForm distributorOrg={distributorOrg} />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!isDirty}>
              Apply Changes
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default BulkEditUsersModal;
